import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Chip,
  Card,
  CardMedia,
  IconButton,
  Rating,
  Alert,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ShoppingCart as ShoppingCartIcon,
  FavoriteBorder as FavoriteIcon,
  Favorite as FavoriteFilledIcon,
  Share as ShareIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  LocalShipping as ShippingIcon,
  Verified as VerifiedIcon,
  Nature as EcoIcon,
  ExpandMore as ExpandMoreIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { ProductService } from '../../services/productService';
import { addToCart } from '../../store/slices/cartSlice';
import { 
  loadUserFavorites, 
  toggleFavorite, 
  selectIsProductFavorite 
} from '../../store/slices/favoritesSlice';
import ProductCard from '../../components/Products/ProductCard';
import { surface, brand, ink, line, status } from '../../theme/tokens';
import Seo from '../../components/Common/Seo';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { isAuthenticated } = useSelector(state => state.auth);
  const isFavorited = useSelector(state => 
    productId ? selectIsProductFavorite(productId)(state) : false
  );
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [expandedAccordion, setExpandedAccordion] = useState('description');
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  useEffect(() => {
    if (productId) {
      loadProduct();
      loadRelatedProducts();
    }
  }, [productId]);

  // Load favorites when auth changes
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(loadUserFavorites());
    }
  }, [isAuthenticated, dispatch]);

  const loadProduct = async () => {
    setLoading(true);
    setError(null);
    try {
      const productData = await ProductService.fetchProduct(productId);
      if (productData) {
        setProduct(productData);
      } else {
        setError('Product not found');
      }
    } catch (error) {
      console.error('Error loading product:', error);
      setError('Failed to load product details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedProducts = async () => {
    try {
      const response = await ProductService.fetchProductsWithPrimaryImages({
        isActive: { eq: true }
      });
      // Get random 4 products (excluding current product)
      const filtered = (response.items || []).filter(p => p.id !== productId);
      const shuffled = filtered.sort(() => 0.5 - Math.random());
      setRelatedProducts(shuffled.slice(0, 4));
    } catch (error) {
      console.error('Error loading related products:', error);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      dispatch(addToCart({
        product: product,
        quantity: quantity
      }));
    }
  };

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= (product?.stock || 1)) {
      setQuantity(newQuantity);
    }
  };

  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      alert('Please login to add products to favorites');
      navigate('/login');
      return;
    }

    if (!productId) return;

    try {
      setIsTogglingFavorite(true);
      await dispatch(toggleFavorite(productId));
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: product.name,
        text: product.shortDescription || product.description,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const handleAccordionChange = (panel) => (_, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="text" height={40} />
            <Skeleton variant="text" height={30} />
            <Skeleton variant="text" height={60} />
            <Box sx={{ mt: 2 }}>
              <Skeleton variant="rectangular" height={48} width={200} />
            </Box>
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Product not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/products')}
        >
          Back to Products
        </Button>
      </Container>
    );
  }

  const images = Array.isArray(product.images) 
    ? product.images 
    : product.images?.items || [];
  
  // Convert signed URLs to public URLs
  const getPublicImageUrl = (url) => {
    // Handle null or undefined
    if (!url) {
      return '/placeholder-product.svg';
    }
    
    // Convert URL object to string if needed
    let urlString;
    if (typeof url === 'string') {
      urlString = url;
    } else if (url instanceof URL) {
      urlString = url.toString();
    } else if (typeof url === 'object' && url.href) {
      urlString = url.href;
    } else {
      console.warn('getPublicImageUrl received unsupported value:', url, typeof url);
      return '/placeholder-product.svg';
    }
    
    const bucketName = 'vidhifarm-storage-219dfeef04fa1-main';
    const region = 'ap-south-1';
    
    if (urlString.includes(`${bucketName}.s3.${region}.amazonaws.com/`)) {
      const urlParts = urlString.split(`${bucketName}.s3.${region}.amazonaws.com/`)[1];
      const filePath = urlParts ? urlParts.split('?')[0] : '';
      return `https://${bucketName}.s3.${region}.amazonaws.com/${filePath}`;
    }
    
    return urlString;
  };
  
  const primaryImageUrl = images.find(img => img.isPrimary)?.url || images[0]?.url;
  const primaryImage = getPublicImageUrl(primaryImageUrl) || '/placeholder-product.svg';

  const hasDiscount = product.discountPrice && product.discountPrice < product.price;
  const discountPercentage = hasDiscount 
    ? Math.round(((product.price - product.discountPrice) / product.price) * 100)
    : 0;

  const isOutOfStock = product.stock === 0;
  const totalPrice = (product.discountPrice || product.price) * quantity;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Seo
        title={product.name}
        description={
          (product.shortDescription || product.description || '').trim().slice(0, 155) ||
          `${product.name} — organic produce grown without chemicals at Vidhi Farm in Birbhum, West Bengal.`
        }
        path={`/products/${productId}`}
        type="product"
        image={primaryImageUrl || undefined}
      />

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ 
        mb: { xs: 2, md: 3 },
        fontSize: { xs: '0.85rem', md: '0.875rem' }
      }}>
        <Link
          color="inherit"
          onClick={() => navigate('/')}
          sx={{ cursor: 'pointer' }}
        >
          Home
        </Link>
        <Link
          color="inherit"
          onClick={() => navigate('/products')}
          sx={{ cursor: 'pointer' }}
        >
          Products
        </Link>
        <Typography 
          color="text.primary"
          sx={{ 
            fontSize: 'inherit',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: { xs: 'nowrap', sm: 'normal' },
            maxWidth: { xs: '150px', sm: 'none' }
          }}
        >
          {product.name}
        </Typography>
      </Breadcrumbs>

      <Grid container spacing={{ xs: 2, md: 4 }}>
        {/* Product Images */}
        <Grid item xs={12} md={6}>
          <Box sx={{ 
            position: { xs: 'static', md: 'sticky' }, 
            top: { md: 24 },
            mb: { xs: 3, md: 0 }
          }}>
            <Card
              elevation={0}
              sx={{
                backgroundColor: surface.card,
                border: `1px solid ${line.subtle}`,
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <CardMedia
                component="img"
                height={{ xs: 250, sm: 300, md: 400 }}
                image={primaryImage}
                alt={product.name}
                sx={{ objectFit: 'cover', backgroundColor: surface.sunken }}
              />
              {images.length > 1 && (
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={1}>
                    {images.slice(0, 4).map((image, index) => (
                      <Grid item xs={3} key={index}>
                        <Box
                          component="img"
                          src={image.url}
                          alt={`${product.name} ${index + 1}`}
                          sx={{
                            width: '100%',
                            height: 60,
                            objectFit: 'cover',
                            borderRadius: 1,
                            cursor: 'pointer',
                            border: selectedImageIndex === index ? 2 : 1,
                            borderColor: selectedImageIndex === index ? brand.brown : line.default
                          }}
                          onClick={() => setSelectedImageIndex(index)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Card>
          </Box>
        </Grid>

        {/* Product Details */}
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            {/* Badges */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {product.isFeatured && (
                <Chip label="Featured" color="primary" size="small" />
              )}
              {hasDiscount && (
                <Chip label={`${discountPercentage}% OFF`} color="error" size="small" />
              )}
              {product.farmingMethod === 'Organic' && (
                <Chip 
                  icon={<EcoIcon />} 
                  label="Organic" 
                  color="success" 
                  size="small" 
                  variant="outlined"
                />
              )}
              {isOutOfStock && (
                <Chip label="Out of Stock" color="error" size="small" />
              )}
            </Box>

            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                lineHeight: { xs: 1.3, md: 1.167 },
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: ink.primary
              }}
            >
              {product.name}
            </Typography>

            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2.5,
                fontSize: { xs: '1rem', md: '1.25rem' },
                lineHeight: { xs: 1.5, md: 1.65 },
                fontWeight: 400,
                color: ink.secondary
              }}
            >
              {product.shortDescription}
            </Typography>

            {/* Category and SKU */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 2 }, 
              mb: 2, 
              alignItems: { xs: 'flex-start', sm: 'center' }
            }}>
              <Chip 
                label={
                  product.category?.name?.replace('_', ' ') || 
                  (typeof product.category === 'string' ? product.category.replace('_', ' ') : 'No Category')
                } 
                variant="outlined" 
                size="small"
              />
              <Typography 
                variant="body2" 
                sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' }, color: ink.muted }}
              >
                SKU: {product.sku}
              </Typography>
            </Box>

            {/* Rating */}
            {product.reviews && product.reviews.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Rating 
                  value={product.reviews.reduce((acc, review) => acc + review.rating, 0) / product.reviews.length} 
                  precision={0.5} 
                  size="small" 
                  readOnly 
                />
                <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                  ({product.reviews.length} review{product.reviews.length !== 1 ? 's' : ''})
                </Typography>
              </Box>
            )}

            {/* Price */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'baseline' }, 
                gap: { xs: 1, sm: 2 }
              }}>
                <Typography 
                  variant="h4" 
                  fontWeight="bold"
                  sx={{ 
                    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' },
                    letterSpacing: '-0.02em',
                    color: brand.mustardInk
                  }}
                >
                  ₹{hasDiscount ? product.discountPrice : product.price}
                </Typography>
                {hasDiscount && (
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      textDecoration: 'line-through', 
                      color: ink.muted,
                      fontSize: { xs: '1.1rem', md: '1.25rem' }
                    }}
                  >
                    ₹{product.price}
                  </Typography>
                )}
              </Box>
              {product.weight && (
                <Typography 
                  variant="body2" 
                  sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' }, color: ink.muted }}
                >
                  {product.weight} kg
                </Typography>
              )}
            </Box>

            {/* Stock Status */}
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontWeight: 600,
                  color: isOutOfStock ? status.error : status.success
                }}
              >
                <VerifiedIcon fontSize="small" />
                {isOutOfStock ? 'Out of stock' : `${product.stock} in stock`}
              </Typography>
            </Box>

            {/* Quantity and Add to Cart */}
            {!isOutOfStock && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>Quantity:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', border: `1px solid ${line.default}`, borderRadius: 2 }}>
                    <IconButton 
                      size="small" 
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                    >
                      <RemoveIcon />
                    </IconButton>
                    <Typography sx={{ px: 2, minWidth: 40, textAlign: 'center' }}>
                      {quantity}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= product.stock}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: ink.primary }}>
                    Total: ₹{totalPrice.toFixed(2)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ShoppingCartIcon />}
                    onClick={handleAddToCart}
                    sx={{ flexGrow: 1 }}
                  >
                    Add to Cart
                  </Button>
                  <IconButton 
                    size="large" 
                    onClick={handleToggleFavorite}
                    color={isFavorited ? 'error' : 'default'}
                    disabled={isTogglingFavorite}
                  >
                    {isTogglingFavorite ? (
                      <CircularProgress size={24} />
                    ) : isFavorited ? (
                      <FavoriteFilledIcon />
                    ) : (
                      <FavoriteIcon />
                    )}
                  </IconButton>
                  <IconButton size="large" onClick={handleShare}>
                    <ShareIcon />
                  </IconButton>
                </Box>

                {/* WhatsApp Inquiry Button */}
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<WhatsAppIcon />}
                    href={`https://wa.me/918583079287?text=Hello%20Vidhi%20Farm!%20I%20have%20a%20question%20about%20this%20product:%20${encodeURIComponent(product.name)}%0A%0AProduct%20Link:%20${encodeURIComponent(window.location.href)}%0A%0APlease%20provide%20more%20details.`}
                    target="_blank"
                    fullWidth
                    sx={{ 
                      color: status.success,
                      borderColor: status.success,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: 'rgba(61,107,51,0.08)',
                        borderColor: status.success
                      }
                    }}
                  >
                    Ask Question via WhatsApp
                  </Button>
                </Box>
              </Box>
            )}

            {/* Shipping Info */}
            <Card
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                bgcolor: 'rgba(61,107,51,0.10)',
                borderColor: 'rgba(61,107,51,0.28)',
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShippingIcon sx={{ color: status.success }} />
                <Typography variant="body2" sx={{ color: status.success, fontWeight: 600 }}>
                  Standard Delivery all over india
                </Typography>
              </Box>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Product Information Accordions */}
      <Box
        sx={{
          mt: { xs: 5, md: 7 },
          mb: { xs: 5, md: 7 },
          border: `1px solid ${line.subtle}`,
          borderRadius: 3,
          overflow: 'hidden',
          backgroundColor: surface.card,
          '& .MuiAccordion-root': { boxShadow: 'none', backgroundColor: 'transparent' },
          '& .MuiAccordion-root:before': { display: 'none' },
          '& .MuiAccordion-root:not(:last-of-type)': { borderBottom: `1px solid ${line.subtle}` },
          '& .MuiAccordionSummary-root': { minHeight: 60 },
        }}
      >
        <Accordion 
          expanded={expandedAccordion === 'description'} 
          onChange={handleAccordionChange('description')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Description</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              {product.description}
            </Typography>
          </AccordionDetails>
        </Accordion>

        {product.nutritionalInfo && (
          <Accordion 
            expanded={expandedAccordion === 'nutrition'} 
            onChange={handleAccordionChange('nutrition')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Nutritional Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1">
                {product.nutritionalInfo}
              </Typography>
            </AccordionDetails>
          </Accordion>
        )}

        <Accordion 
          expanded={expandedAccordion === 'details'} 
          onChange={handleAccordionChange('details')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Product Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell><strong>Farming Method</strong></TableCell>
                  <TableCell>{product.farmingMethod}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Weight</strong></TableCell>
                  <TableCell>{product.weight} kg</TableCell>
                </TableRow>
                {product.dimensions && (
                  <TableRow>
                    <TableCell><strong>Dimensions</strong></TableCell>
                    <TableCell>{product.dimensions}</TableCell>
                  </TableRow>
                )}
                {product.harvestDate && (
                  <TableRow>
                    <TableCell><strong>Harvest Date</strong></TableCell>
                    <TableCell>{new Date(product.harvestDate).toLocaleDateString()}</TableCell>
                  </TableRow>
                )}
                {product.expiryDate && (
                  <TableRow>
                    <TableCell><strong>Best Before</strong></TableCell>
                    <TableCell>{new Date(product.expiryDate).toLocaleDateString()}</TableCell>
                  </TableRow>
                )}
                {product.tags && product.tags.length > 0 && (
                  <TableRow>
                    <TableCell><strong>Tags</strong></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {product.tags.map(tag => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ fontWeight: 700, letterSpacing: '-0.015em', color: ink.primary, mb: 3 }}
          >
            You Might Also Like
          </Typography>
          <Grid container spacing={3}>
            {relatedProducts.map(relatedProduct => (
              <Grid item xs={12} sm={6} md={3} key={relatedProduct.id}>
                <Box 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/products/${relatedProduct.id}`)}
                >
                  <ProductCard
                    product={relatedProduct}
                    onAddToCart={(product) => dispatch(addToCart({
                      product: product,
                      quantity: 1
                    }))}
                    onToggleFavorite={async (product) => {
                      if (!isAuthenticated) {
                        alert('Please login to add products to favorites');
                        navigate('/login');
                        return;
                      }
                      try {
                        await dispatch(toggleFavorite(product.id));
                      } catch (error) {
                        console.error('Error toggling favorite:', error);
                      }
                    }}
                    onLoginRequired={() => {
                      alert('Please login to add products to favorites');
                      navigate('/login');
                    }}
                    onShare={() => {}}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Container>
  );
};

export default ProductDetailPage;